import type { ReduxSagaModel } from './types';
import { sgselect } from '@/utils/reduxSaga';

/**
 * App-shell state: whether the sidebar is collapsed to its icon rail.
 *
 * Two values rather than one, because Home collapses the sidebar for the length of a visit
 * without that becoming the user's preference. `sidebarCollapsed` is the preference, kept in
 * localStorage. `sidebarOverride` is a page's temporary say: `null` when no page has one, else
 * the state to show instead. Toggling while an override is active only flips the override, so
 * expanding the rail on Home keeps it expanded until the user leaves Home, and leaving puts
 * back whatever they had chosen before.
 *
 * Not in `resetAllModelStatesOnWorkspaceSwitch`: the preference belongs to the browser, not to
 * a workspace.
 */

const STORAGE_KEY = 'beem-sidebar-collapsed';

function readPreference(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writePreference(collapsed: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(collapsed));
  } catch {
    // Storage can be unavailable (private mode, blocked site data). The toggle still works for
    // this session, which is all that matters.
  }
}

export type State = {
  sidebarCollapsed: boolean;
  sidebarOverride: boolean | null;
};

const getInitialState = (): State => ({
  sidebarCollapsed: readPreference(),
  sidebarOverride: null,
});

export type SaveSidebarAction = {
  type: 'saveSidebar' | 'layout/saveSidebar';
  payload: Partial<State>;
};

export type ToggleSidebarEffect = { type: 'layout/toggleSidebar' };

/** Pass `collapsed: null` to drop the override, e.g. when the page that set it unmounts. */
export type SetSidebarOverrideEffect = {
  type: 'layout/setSidebarOverride';
  payload: { collapsed: boolean | null };
};

/** What the sidebar actually shows. */
export const isSidebarCollapsed = (state: State): boolean =>
  state.sidebarOverride ?? state.sidebarCollapsed;

const LayoutModel: ReduxSagaModel<State, { saveSidebar: SaveSidebarAction }> = {
  namespace: 'layout',
  state: getInitialState(),
  effects: {
    *toggleSidebar(_, { put }) {
      const { sidebarCollapsed, sidebarOverride } = yield* sgselect((s) => s.layout);
      if (sidebarOverride !== null) {
        yield put<SaveSidebarAction>({
          type: 'saveSidebar',
          payload: { sidebarOverride: !sidebarOverride },
        });
        return;
      }
      writePreference(!sidebarCollapsed);
      yield put<SaveSidebarAction>({
        type: 'saveSidebar',
        payload: { sidebarCollapsed: !sidebarCollapsed },
      });
    },
    *setSidebarOverride({ payload }: { payload: SetSidebarOverrideEffect['payload'] }, { put }) {
      yield put<SaveSidebarAction>({
        type: 'saveSidebar',
        payload: { sidebarOverride: payload.collapsed },
      });
    },
  },
  reducers: {
    resetAll() {
      return getInitialState();
    },
    saveSidebar(state, { payload }) {
      return { ...state, ...payload };
    },
  },
};

export default LayoutModel;
